import { Fragment, useState, memo, useCallback, useMemo } from 'react'
import { CREATE_RESUME_ID_PLACEHOLDER } from 'hooks/resume'
import { useUploadFile } from 'hooks/filestack'
import { useQuickCommit } from 'hooks/useQuickCommit'
import { graphql, useFragment } from 'react-relay'
import { IconButton, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import MoreButtonCard from 'components/MoreButtonCard'
import Condition from 'components/Condition'
import { Create } from '@mui/icons-material'
import { PickerFileMetadata } from 'filestack-js'
import { SocialLinksFields_ParseResumeMutation } from '__generated__/SocialLinksFields_ParseResumeMutation.graphql'
import { SocialLinksFields_User$key } from '__generated__/SocialLinksFields_User.graphql'
import { useResponsiveBreakpoint } from 'hooks/useResponsiveBreakpoint'
import useStyles from './styles'
import SocialLinksEditDialog from './components/SocialLinksEditDialog'
import SocialLink from './components/SocialLink'
import SocialLinksUpdateResumeDialog from './components/SocialLinksUpdateResumeDialog'

interface ISocialLinksFieldsProps {
  editable?: boolean
  user: SocialLinksFields_User$key
}

const filestackAccept = [
  'text/plain',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]

function SocialLinksFields(props: ISocialLinksFieldsProps) {
  const { classes } = useStyles()
  const theme = useTheme()
  const { editable, user: userFragmentRef } = props

  const user = useFragment(graphql`
    fragment SocialLinksFields_User on User {
      profile {
        urlBlog
        urlDribbble
        urlGithub
        urlLinkedin
      }
      resume {
        url
      }
      ...SocialLinksEditDialog_User
    }
  `, userFragmentRef)

  const [resumeUploadData, setResumeUploadData] = useState<PickerFileMetadata>()
  const [isEditMode, setEditMode] = useState(false)

  const isMobile = useResponsiveBreakpoint(theme.breakpoints.values.sm)

  const { execute: executeResumeUpload, loading: resumeLoading, error: uploadError } = useQuickCommit<SocialLinksFields_ParseResumeMutation>(
    graphql`
      mutation SocialLinksFields_ParseResumeMutation($input: CreateResumeInput!) {
        createResume(input: $input) {
          resume {
            id

            user {
              resume {
                id
                ...resume_useIsImportingResume
              }
            }

            ...resume_useIsImportingResume
          }
        }
      }
    `,
  )

  const filestackOptions = useMemo(() => {
    return {
      options: {
        accept: filestackAccept,
      },
      onChange: async (data: PickerFileMetadata) => {
        setResumeUploadData(data)
      },
    }
  }, [])

  const { openPicker, error: pickerError } = useUploadFile(filestackOptions)

  const handleImport = useCallback(async (copyToProfile: boolean) => {
    if (!resumeUploadData) return

    await executeResumeUpload(
      {
        input: {
          url: resumeUploadData.url,
          filename: resumeUploadData.filename,
          mimetype: resumeUploadData.mimetype,
          copyToProfile: copyToProfile,
        },
      },
      {
        mutatingNodeId: copyToProfile ? CREATE_RESUME_ID_PLACEHOLDER : undefined,
      },
    )

    setResumeUploadData(undefined)
  }, [resumeUploadData, executeResumeUpload])

  // TODO: restore error handling
  const errors = []
  const numSocialLinks = Object.values(user?.profile || {}).filter(Boolean).length
  const hasAnyLink = numSocialLinks > 0
  const breakPoint = (numSocialLinks <= 3 ? 321 : 397) + (editable ? 48 : 0)
  const isMatch = useResponsiveBreakpoint(breakPoint - 1)

  const socialLinks = (
    <Fragment>
      <SocialLink url={user?.resume?.url} type="Resume" error={null} />
      <SocialLink url={user?.profile?.urlBlog} type="Website or Blog" error={null} />
      <SocialLink url={user?.profile?.urlGithub} type="Github" error={null} />
      <SocialLink url={user?.profile?.urlLinkedin} type="LinkedIn" error={null} />
      <SocialLink url={user?.profile?.urlDribbble} type="Dribbble" error={null} />
    </Fragment>
  )

  const handleOpenResumeUpload = useCallback(() => {
    openPicker()
    setEditMode(false)
  }, [openPicker])

  return (
    <Fragment>
      <Condition condition={editable || hasAnyLink}>
        {numSocialLinks <= 1 && (
          <Fragment>
            {socialLinks}
          </Fragment>
        )}

        {numSocialLinks > 1 && (
          <Fragment>
            <Condition condition={!isMatch}>
              {socialLinks}
            </Condition>

            <Condition condition={isMatch}>
              <MoreButtonCard>
                {socialLinks}
              </MoreButtonCard>
            </Condition>
          </Fragment>
        )}

        {isEditMode && (
          <SocialLinksEditDialog
            user={user}
            onClose={() => setEditMode(false)}
            onResumeUploadOpen={handleOpenResumeUpload}
          />
        )}

        {resumeUploadData && (
          <SocialLinksUpdateResumeDialog
            open
            resumeLoading={resumeLoading}
            resumeError={uploadError?.message || pickerError?.message}
            onClose={() => setResumeUploadData(undefined)}
            onAttach={() => handleImport(false)}
            onImport={() => handleImport(true)}
          />
        )}

        {editable && (
          <div className={classes.edit}>
            {!hasAnyLink && (
              <Typography variant="body2" className={classes.description}>
                {isMobile ? 'Links' : 'Your Links'}
              </Typography>
            )}

            <div className={classes.button}>
              <IconButton onClick={() => setEditMode(true)} size="large">
                <Create />
              </IconButton>
            </div>

            {errors.length > 0 && (
              <Typography variant="body1" className={classes.error}>
                Please fix the errors in your social links
              </Typography>
            )}
          </div>
        )}
      </Condition>
    </Fragment>
  )
}

export default memo(SocialLinksFields)
